import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import { CATEGORIES, RADII, CERT_FILTERS, FILTER_TAGS, SELLER_TYPES } from "@/lib/constants";
import FarmMap from "@/components/FarmMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter,
} from "@/components/ui/sheet";
import { Search, SlidersHorizontal, MapPin, X, Loader2, BadgeCheck, Truck, Store } from "lucide-react";
import { toast } from "sonner";

export default function LiveMap() {
  const navigate = useNavigate();
  const [farms, setFarms] = useState([]);
  const [center, setCenter] = useState(null);
  const [radius, setRadius] = useState(25);
  const [category, setCategory] = useState("all");
  const [sellerTypes, setSellerTypes] = useState([]);
  const [certStatus, setCertStatus] = useState("any");
  const [tags, setTags] = useState([]);
  const [fulfilment, setFulfilment] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searched, setSearched] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/farms");
      setFarms(res.data);
    } catch (e) { toast.error("Could not load farms"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const runSearch = async (opts = {}) => {
    const q = opts.query !== undefined ? opts.query : query;
    setLoading(true);
    setSelected(null);
    try {
      const params = {
        radius: opts.radius ?? radius,
      };
      const cat = opts.category ?? category;
      const cs = opts.certStatus ?? certStatus;
      const tg = opts.tags ?? tags;
      const ful = opts.fulfilment ?? fulfilment;
      const st = opts.sellerTypes ?? sellerTypes;
      if (cat !== "all") params.category = cat;
      if (cs && cs !== "any") params.cert_status = cs;
      if (tg && tg.length) params.tags = tg.join(",");
      if (st && st.length) params.seller_type = st.join(",");
      if (ful !== "all") params.fulfilment = ful;

      let centerToUse = center;
      if (q && q.trim()) {
        // try as postcode first, else place search
        try {
          const g = await api.get("/geocode/postcode", { params: { postcode: q } });
          centerToUse = [g.data.lat, g.data.lng];
        } catch {
          const pl = await api.get("/geocode/places", { params: { q } });
          if (pl.data.length) centerToUse = [pl.data[0].lat, pl.data[0].lng];
          else { toast.error("Location not found. Try a UK postcode or town."); setLoading(false); return; }
        }
        setCenter(centerToUse);
      }
      if (centerToUse) {
        params.lat = centerToUse[0];
        params.lng = centerToUse[1];
      }
      const res = await api.get("/farms/search", { params });
      setFarms(res.data.farms);
      setSearched(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };

  const clearFilters = () => {
    setCategory("all"); setCertStatus("any"); setTags([]); setFulfilment("all"); setSellerTypes([]);
    setQuery(""); setCenter(null); setSearched(false); setSelected(null);
    loadAll();
  };

  const toggleTag = (t) => setTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  const toggleSellerType = (t) => setSellerTypes((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);

  const activeFilterCount = (category !== "all" ? 1 : 0) + (certStatus !== "any" ? 1 : 0) + tags.length + sellerTypes.length + (fulfilment !== "all" ? 1 : 0);

  return (
    <div className="relative h-screen w-full pt-16 overflow-hidden">
      {/* Map */}
      <div className="absolute inset-0 top-16">
        <FarmMap farms={farms} center={center} radiusMiles={searched ? radius : null}
          selectedId={selected?.id} onSelect={setSelected} />
      </div>

      {/* Top overlay search bar */}
      <div className="absolute top-16 left-0 right-0 z-[800] pointer-events-none">
        <div className="max-w-3xl mx-auto px-3 pt-3 pointer-events-auto">
          <div className="bg-card/95 backdrop-blur rounded-2xl shadow-md border border-border p-2 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="map-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                placeholder="Search postcode or town (e.g. BS1 4DJ, Bristol)"
                className="pl-9 border-0 shadow-none focus-visible:ring-0 bg-transparent"
              />
            </div>
            <Select value={String(radius)} onValueChange={(v) => { setRadius(Number(v)); if (searched) runSearch({ radius: Number(v) }); }}>
              <SelectTrigger className="w-[104px] shrink-0" data-testid="map-radius-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[1200]">
                {RADII.map((r) => <SelectItem key={r} value={String(r)}>{r} miles</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => runSearch()} data-testid="map-search-button" className="shrink-0">
              <Search className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Search</span>
            </Button>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="secondary" className="shrink-0 relative" data-testid="map-open-filters-button">
                  <SlidersHorizontal className="h-4 w-4" />
                  {activeFilterCount > 0 && <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 grid place-items-center bg-[color:var(--fg-wood-600)] text-white">{activeFilterCount}</Badge>}
                </Button>
              </SheetTrigger>
              <SheetContent className="z-[1300] w-[360px] flex flex-col overflow-y-auto">
                <SheetHeader><SheetTitle>Filter producers</SheetTitle></SheetHeader>
                <div className="flex-1 space-y-6 py-4">
                  <div className="space-y-2">
                    <Label>Seller type</Label>
                    <div className="grid grid-cols-2 gap-1.5" data-testid="map-seller-types">
                      {SELLER_TYPES.map((t) => (
                        <label key={t} className={`flex items-center gap-2 rounded-lg border p-2 cursor-pointer text-sm ${sellerTypes.includes(t) ? "border-primary bg-secondary" : "border-border"}`}>
                          <Checkbox checked={sellerTypes.includes(t)} onCheckedChange={() => toggleSellerType(t)} data-testid="map-seller-type" /> <span className="leading-tight">{t}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Certification status</Label>
                    <Select value={certStatus} onValueChange={setCertStatus}>
                      <SelectTrigger data-testid="map-cert-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="z-[1400]">
                        {CERT_FILTERS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Product category</Label>
                    <Select value={category} onValueChange={(v) => { setCategory(v); setTags([]); }}>
                      <SelectTrigger data-testid="map-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="z-[1400]">
                        <SelectItem value="all">All categories</SelectItem>
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {category !== "all" && (FILTER_TAGS[category] || []).length > 0 && (
                    <div className="space-y-2" data-testid="map-practice-tags">
                      <Label>{category} production details</Label>
                      <div className="space-y-1.5">
                        {FILTER_TAGS[category].map((t) => (
                          <label key={t} className={`flex items-center gap-2 rounded-lg border p-2.5 cursor-pointer text-sm ${tags.includes(t) ? "border-primary bg-secondary" : "border-border"}`}>
                            <Checkbox checked={tags.includes(t)} onCheckedChange={() => toggleTag(t)} data-testid="map-practice-tag" /> {t}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Fulfilment</Label>
                    <RadioGroup value={fulfilment} onValueChange={setFulfilment} data-testid="map-fulfilment-radio">
                      {[["all", "Any"], ["collection", "Collection"], ["delivery", "Delivery"]].map(([v, l]) => (
                        <label key={v} className="flex items-center gap-2 rounded-lg border border-border p-2.5 cursor-pointer">
                          <RadioGroupItem value={v} /> {l}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                </div>
                <SheetFooter className="flex-row gap-2">
                  <Button variant="ghost" className="flex-1" onClick={clearFilters} data-testid="map-clear-filters-button">Clear</Button>
                  <Button className="flex-1" onClick={() => { setFiltersOpen(false); runSearch(); }} data-testid="map-apply-filters-button">Apply</Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs">
            <Badge variant="secondary" className="bg-card/90">{farms.length} producer{farms.length !== 1 ? "s" : ""} {searched ? "in range" : "nationwide"}</Badge>
            {searched && <Button variant="ghost" size="sm" className="h-6 text-xs bg-card/80" onClick={clearFilters}>Reset</Button>}
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 top-16 z-[700] grid place-items-center bg-background/30" data-testid="map-loading-state">
          <div className="bg-card rounded-full px-4 py-2 shadow-md flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading producers…
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && farms.length === 0 && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-8 z-[800] px-4 w-full max-w-md" data-testid="map-empty-state">
          <Card className="rounded-2xl shadow-lg p-6 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-secondary grid place-items-center mb-3">
              <MapPin className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-semibold text-lg">No producers here yet</h3>
            <p className="text-muted-foreground text-sm mt-1">Check back soon — or if you're a local food producer, list your business.</p>
            <Button className="mt-4 bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white" onClick={() => navigate("/sell/register")}>Become a seller</Button>
          </Card>
        </div>
      )}

      {/* Selected farm preview card */}
      {selected && (
        <div className="absolute left-1/2 -translate-x-1/2 sm:left-4 sm:translate-x-0 bottom-6 z-[850] w-[92%] sm:w-[360px]">
          <Card className="rounded-2xl shadow-lg overflow-hidden" data-testid="map-farm-preview-card">
            <div className="relative h-36 bg-secondary">
              {selected.photo_url ? (
                <img src={imgUrl(selected.photo_url)} alt={selected.farm_name} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full grid place-items-center text-muted-foreground"><Store className="h-8 w-8" /></div>
              )}
              <button onClick={() => setSelected(null)} className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-full bg-card/90 shadow">
                <X className="h-4 w-4" />
              </button>
              <div className="absolute bottom-2 left-2 flex gap-1.5 flex-wrap">
                {selected.seller_type && <Badge className="bg-card/95 text-foreground border border-border" data-testid="map-preview-seller-type">{selected.seller_type}</Badge>}
                {selected.certification?.verified && <Badge className="bg-[#E6F2E8] text-[#1F5A2F] border border-[rgba(31,90,47,0.18)]"><BadgeCheck className="h-3 w-3 mr-1" />{selected.certification.label}</Badge>}
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-lg leading-tight">{selected.farm_name}</h3>
                {selected.distance != null && <Badge variant="secondary" className="shrink-0">{selected.distance} mi</Badge>}
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{selected.description || `${selected.town}, ${selected.county}`}</p>
              <div className="flex gap-1.5 mt-2">
                {selected.collection_available && <Badge className="bg-[#F3EEE6] text-[#5A3A1F] border border-[rgba(90,58,31,0.18)]"><Store className="h-3 w-3 mr-1" />Collection</Badge>}
                {selected.delivery_available && <Badge className="bg-[#E7F1F3] text-[#1F4E57] border border-[rgba(31,78,87,0.18)]"><Truck className="h-3 w-3 mr-1" />Delivery</Badge>}
              </div>
              <Button className="w-full mt-3" onClick={() => navigate(`/farms/${selected.id}`)} data-testid="map-farm-preview-view-farm-button">
                View Farm
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
